/**
 * Kit controls (ui-ux §19.2, §19.2b, §19.3, §19.11, §19.13): buttons, «Главное», painted tiles, wax seals, segmented
 * tabs, chips, badges, the ruler slider, the seal toggle, text inputs, swatches, roundel options.
 * Each returns a plain element built with `h()`; live values come in as signals. Owner: UI/Visual (F3).
 */
import { isSignal, type ReadonlySignal } from '../../shared';
import { h, type Child } from '../dom';
import { glyph, type ShellGlyph } from '../icons/shell.svg';
import { haptic, type HapticId } from '../feedback/haptics';

let uid = 0;

export type ButtonVariant = 'primary' | 'secondary' | 'reward' | 'main' | 'danger' | 'quill' | 'text' | 'chrome' | 'seal';

export interface ButtonOpts {
  label?: Child;
  variant?: ButtonVariant;
  size?: 'sm' | 'block' | 'icon';
  /** Leading shell glyph or a node (icon-only when there is no label: then `ariaLabel` is required). */
  icon?: ShellGlyph | Node;
  ariaLabel?: string;
  /** `true` or a reason string: `aria-disabled` + the reason as `title`/tooltip text (U11: every lock states why). */
  disabled?: boolean | string;
  onClick?: (e: MouseEvent) => void;
  haptic?: HapticId;
  /** Reward / «Главное» sheen (granted by the OrnamentBudget; ≤ 2 running per viewport). */
  sheen?: boolean;
  /** «Главное»: sub-line and count seal. */
  sub?: Child;
  count?: number | string;
  data?: Record<string, string>;
  type?: 'button' | 'submit';
}

/** `.mc-btn` in all its variants (one primary per view, U3). */
export function button(o: ButtonOpts): HTMLButtonElement {
  const variant = o.variant ?? 'secondary';
  const cls = ['mc-btn', `mc-btn--${variant}`];
  if (o.size) cls.push(`mc-btn--${o.size}`);
  if (!o.label && o.icon && variant !== 'seal') cls.push('mc-btn--icon');
  const attrs: Record<string, string | boolean> = { type: o.type ?? 'button' };
  if (o.ariaLabel) attrs['aria-label'] = o.ariaLabel;
  if (o.disabled) { attrs['aria-disabled'] = 'true'; if (typeof o.disabled === 'string') { attrs.title = o.disabled; attrs['data-reason'] = o.disabled; } }
  if (o.sheen) attrs['data-sheen'] = 'on';
  for (const [k, v] of Object.entries(o.data ?? {})) attrs[`data-${k}`] = v;
  const icon = typeof o.icon === 'string' ? glyph(o.icon, { size: variant === 'seal' ? 22 : 22 }) : o.icon ?? null;
  const children: Child[] = [];
  if (variant === 'main') {
    children.push(h('span', { class: 'mc-btn__icon' }, icon));
    children.push(h('span', { class: 'mc-btn__label' }, o.label ?? null, o.sub ? h('span', { class: 'mc-btn__sub' }, o.sub) : null));
    if (o.count !== undefined) children.push(seal(o.count));
  } else {
    if (icon) children.push(icon);
    if (o.label !== undefined) children.push(h('span', { class: 'mc-btn__label' }, o.label));
  }
  return h('button', {
    class: cls.join(' '), attrs,
    on: {
      click: (e) => {
        if (attrs['aria-disabled']) { e.preventDefault(); return; }
        if (o.haptic) haptic(o.haptic);
        o.onClick?.(e);
      },
    },
  }, ...children);
}

/** A wax seal with a number or short text (cost badges, counts). */
export function seal(text: number | string | ReadonlySignal<string | number>, o: { size?: 'sm' | 'lg'; ground?: 'gold'; state?: 'short'; label?: string } = {}): HTMLSpanElement {
  const cls = `mc-seal${o.size ? ` mc-seal--${o.size}` : ''}`;
  const attrs: Record<string, string> = {};
  if (o.ground) attrs['data-ground'] = o.ground;
  if (o.state) attrs['data-state'] = o.state;
  if (o.label) attrs['aria-label'] = o.label; else attrs['aria-hidden'] = 'true';
  return h('span', { class: cls, attrs }, isSignal<string | number>(text) ? text : String(text));
}

export type TileGround = 'lapis' | 'vermilion' | 'verdigris' | 'folium' | 'gold';
export type TileState = 'idle' | 'ready' | 'short' | 'queued' | 'used' | 'vow' | 'react';

export interface TileOpts {
  name: string;
  icon: Node;
  ground: TileGround;
  cost?: number;
  kind?: 'prayer' | 'verb';
  state?: TileState | ReadonlySignal<TileState>;
  ariaLabel: string;
  /** Verb tiles: the short-of-Grace timer plate («6 мин»). */
  timer?: string;
  disabledReason?: string;
  reactMs?: number;
  onClick?: () => void;
  key?: string;
}

/** The pointed-arch outline (100×100, stretched) the react-window ring strokes. */
const ARCH_RING = 'M1 99V35L2.5 28L7 21.2L14.4 14.8L24.4 9L36.7 4L50 1L63.3 4L75.6 9L85.6 14.8L93 21.2L97.5 28L99 35V99';

/** `.mc-tile`: an arched painted tile with a banderole and a wax-seal cost (prayers, verbs, menus). */
export function tile(o: TileOpts): HTMLButtonElement {
  const ns = 'http://www.w3.org/2000/svg';
  const ring = document.createElementNS(ns, 'svg');
  ring.setAttribute('class', 'mc-tile__ring');
  ring.setAttribute('viewBox', '0 0 100 100');
  ring.setAttribute('preserveAspectRatio', 'none');
  ring.setAttribute('aria-hidden', 'true');
  const path = document.createElementNS(ns, 'path');
  path.setAttribute('d', ARCH_RING);
  path.setAttribute('pathLength', '1');
  ring.appendChild(path);
  const iconBox = h('span', { class: 'mc-tile__icon' }, o.icon);
  const attrs: Record<string, string | ReadonlySignal<string>> = {
    type: 'button', 'data-ground': o.ground, 'aria-label': o.ariaLabel,
    'data-state': (o.state ?? 'idle') as string | ReadonlySignal<string>,
  };
  if (o.key) attrs['data-key'] = o.key;
  if (o.disabledReason) { attrs['aria-disabled'] = 'true'; attrs.title = o.disabledReason; }
  const el = h('button', {
    class: `mc-tile mc-tile--${o.kind ?? 'prayer'}`, attrs,
    style: o.reactMs ? { '--react-ms': `${o.reactMs}ms` } : undefined,
    on: { click: () => { if (!o.disabledReason) o.onClick?.(); } },
  },
    h('span', { class: 'mc-tile__frame', attrs: { 'aria-hidden': 'true' } }),
    h('span', { class: 'mc-tile__gilt', attrs: { 'aria-hidden': 'true' } }),
    h('span', { class: 'mc-tile__ground', attrs: { 'aria-hidden': 'true' } }),
    iconBox,
    ring,
    h('span', { class: 'mc-tile__badge', attrs: { 'aria-hidden': 'true' } }),
    h('span', { class: 'mc-tile__name', attrs: { 'aria-hidden': 'true' } }, h('span', null, o.name)),
    o.cost !== undefined ? seal(o.cost, { size: o.kind === 'verb' ? 'sm' : undefined, state: (o.state === 'short' ? 'short' : undefined) }) : null,
    o.timer ? h('span', { class: 'mc-tile__timer', attrs: { 'aria-hidden': 'true' } }, o.timer) : null,
  );
  const costEl = el.querySelector('.mc-seal');
  costEl?.classList.add('mc-tile__cost');
  return el;
}

export interface SegItem { id: string; label: string }

/** Segmented tabs (`role=tablist`) or a boxed choice row (`aria-pressed` buttons). */
export function segmented(o: { items: SegItem[]; value: ReadonlySignal<string>; onChange: (id: string) => void; boxed?: boolean; label: string }): HTMLDivElement {
  const role = o.boxed ? 'group' : 'tablist';
  const root = h('div', { class: `mc-seg${o.boxed ? ' mc-seg--boxed' : ''}`, attrs: { role, 'aria-label': o.label } });
  if (!o.boxed) {
    // overflow fades (§19.3): mark which ends hide content
    const sync = (): void => {
      root.classList.toggle('is-overflow-end', root.scrollLeft + root.clientWidth < root.scrollWidth - 2);
      root.classList.toggle('is-overflow-start', root.scrollLeft > 2);
    };
    root.addEventListener('scroll', sync, { passive: true });
    if (typeof ResizeObserver !== 'undefined') new ResizeObserver(sync).observe(root);
  }
  for (const it of o.items) {
    const sel = { get: () => String(o.value.get() === it.id), subscribe: (fn: (v: string) => void) => o.value.subscribe((v) => fn(String(v === it.id))) };
    const attrs: Record<string, string | ReadonlySignal<string>> = o.boxed ? { type: 'button', 'aria-pressed': sel } : { type: 'button', role: 'tab', 'aria-selected': sel };
    root.appendChild(h('button', { class: 'mc-seg__item', attrs, on: { click: () => { if (o.value.get() !== it.id) { haptic('select'); o.onChange(it.id); } } } }, it.label));
  }
  return root;
}

/** A filter chip (`aria-pressed`). */
export function filterChip(label: string, pressed: ReadonlySignal<boolean>, onToggle: () => void): HTMLButtonElement {
  return h('button', {
    class: 'mc-chip-filter',
    attrs: { type: 'button', 'aria-pressed': { get: () => String(pressed.get()), subscribe: (fn: (v: string) => void) => pressed.subscribe((v) => fn(String(v))) } },
    on: { click: onToggle },
  }, label);
}

/** A currency / meter chip: an icon (emblem or ink glyph, 22 px) + a tabular value; `night` for chrome strips. */
export function chip(o: { icon?: Node; value: Child; label: string; night?: boolean; ring?: number; badge?: number }): HTMLSpanElement {
  const iconNode = o.ring !== undefined && o.icon ? ringed(o.icon, o.ring) : o.icon ?? null;
  return h('span', { class: `mc-chip${o.night ? ' mc-chip--night' : ''}`, attrs: { role: 'img', 'aria-label': o.label } },
    iconNode, h('span', { class: 'num', attrs: { 'aria-hidden': 'true' } }, o.value), o.badge ? badge(o.badge) : null);
}

/** Wraps an icon in a progress ring (candle regen, 0..1). */
export function ringed(icon: Node, p: number): HTMLSpanElement {
  const ns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('class', 'mc-ringsvg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.innerHTML = '<circle class="bg" cx="12" cy="12" r="10.6" pathLength="100"/><circle class="fg" cx="12" cy="12" r="10.6" pathLength="100"/>';
  return h('span', { class: 'mc-chip__ring', style: { '--p': String(Math.max(0, Math.min(1, p))) } }, svg, icon);
}

/** A vermilion count badge (or a gold dot with `n = 0`). */
export function badge(n: number): HTMLSpanElement {
  return n > 0 ? h('span', { class: 'mc-badge', attrs: { 'aria-hidden': 'true' } }, n > 99 ? '99+' : String(n)) : h('span', { class: 'mc-badge mc-badge--dot', attrs: { 'aria-hidden': 'true' } });
}

/** The ruler slider «Линейка» (44 px, ink ticks, wax-seal thumb) with its label and live value. */
export function rulerSlider(o: { label: string; min: number; max: number; step?: number; value: number; format?: (v: number) => string; onInput: (v: number) => void; onCommit?: (v: number) => void }): HTMLDivElement {
  const fmt = o.format ?? ((v: number) => String(v));
  const valueEl = h('span', { class: 'mc-ruler-row__value', attrs: { 'aria-hidden': 'true' } }, fmt(o.value));
  const id = `mc-ruler-${++uid}`;
  const input = h('input', {
    class: 'mc-ruler',
    attrs: { type: 'range', id, min: o.min, max: o.max, step: o.step ?? 1, value: o.value, 'aria-valuetext': fmt(o.value) },
    style: { '--fill': String((o.value - o.min) / (o.max - o.min)) },
    on: {
      input: (e) => {
        const v = Number((e.target as HTMLInputElement).value);
        const t = e.target as HTMLInputElement;
        t.style.setProperty('--fill', String((v - o.min) / (o.max - o.min)));
        t.setAttribute('aria-valuetext', fmt(v));
        valueEl.textContent = fmt(v);
        o.onInput(v);
      },
      change: (e) => o.onCommit?.(Number((e.target as HTMLInputElement).value)),
    },
  });
  return h('div', { class: 'mc-ruler-row' }, h('label', { class: 'mc-ruler-row__label', attrs: { for: id } }, o.label), input, valueEl);
}

/** The seal toggle «Печать» (`role=switch`, 44×44): off = a vellum disc, on = a stamped vermilion seal with a paw. */
export function sealToggle(o: { checked: boolean | ReadonlySignal<boolean>; label: string; onChange: (v: boolean) => void; disabledReason?: string }): HTMLButtonElement {
  const sig = isSignal<boolean>(o.checked) ? o.checked : null;
  let local = sig ? sig.get() : o.checked as boolean;
  const checkedAttr = sig ? { get: () => String(sig.get()), subscribe: (fn: (v: string) => void) => sig.subscribe((v) => fn(String(v))) } : String(local);
  const attrs: Record<string, string | ReadonlySignal<string>> = { type: 'button', role: 'switch', 'aria-label': o.label, 'aria-checked': checkedAttr };
  if (o.disabledReason) { attrs['aria-disabled'] = 'true'; attrs.title = o.disabledReason; }
  const el = h('button', {
    class: 'mc-sealtoggle', attrs,
    on: {
      click: () => {
        if (o.disabledReason) return;
        local = !(sig ? sig.get() : local);
        if (!sig) el.setAttribute('aria-checked', String(local));
        haptic(local ? 'seal' : 'select');
        o.onChange(local);
      },
    },
  });
  return el;
}

/** A text input in the kit skin (≥ 16 px, 48 px tall) with an optional label and error line. */
export function textField(o: { label: string; value?: string; placeholder?: string; onInput?: (v: string) => void; error?: string; enterkeyhint?: string }): HTMLLabelElement {
  return h('label', { class: 'mc-field' },
    h('span', { class: 'mc-field__label' }, o.label),
    h('input', {
      class: 'mc-input',
      attrs: { type: 'text', value: o.value ?? '', placeholder: o.placeholder ?? '', autocomplete: 'off', enterkeyhint: o.enterkeyhint ?? 'done', 'aria-invalid': o.error ? 'true' : false },
      on: { input: (e) => o.onInput?.((e.target as HTMLInputElement).value) },
    }),
    o.error ? h('span', { class: 'mc-field__error' }, o.error) : null);
}

/** A colour swatch (44 px circle); `color` is a CSS colour or a pigment var. */
export function swatch(o: { color: string; label: string; checked?: boolean; locked?: boolean; onPick?: () => void }): HTMLButtonElement {
  const attrs: Record<string, string | boolean> = { type: 'button', role: 'radio', 'aria-label': o.label, 'aria-checked': String(!!o.checked) };
  if (o.locked) { attrs['data-locked'] = true; attrs['aria-disabled'] = 'true'; }
  return h('button', { class: 'mc-swatch', attrs, style: { '--_c': o.color }, on: { click: () => { if (!o.locked) { haptic('select'); o.onPick?.(); } } } });
}

/** A roundel option (64 px circle, ink ring + tier ring; selected = gold ring). */
export function roundelOption(o: { content: Node; label: string; selected?: boolean; size?: 44 | 48 | 64; onPick?: () => void }): HTMLButtonElement {
  return h('button', {
    class: `mc-roundel${o.size && o.size !== 64 ? ` mc-roundel--${o.size}` : ''}`,
    attrs: { type: 'button', role: 'radio', 'aria-label': o.label, 'aria-checked': String(!!o.selected) },
    on: { click: () => { haptic('select'); o.onPick?.(); } },
  }, o.content);
}
